import React from 'react';
import {
  SectionWrapper,
  SectionHeader,
  Title,
  Caption,
  MetricSection,
  MetricLabel,
  MetricValue,
  Row,
  ProgressTrack,
  ProgressFill,
  formatDuration,
  computeTimeFromIntervals,
} from './common';
import CollapsibleSection, {
  CollapsibleSectionHeader,
  CollapsibleSectionLabel,
} from '../CollapsibleSection';
import styled from 'styled-components';
import StepQuestionsResultsTable from './StepQuestionsResults/StepQuestionsResultsTable';
import {
  AssessmentTestSessionDetailResponse,
  AssessmentTestStep,
  AssessmentTestResult,
  AssessmentTestStepAnswer,
} from '../../../api/admin';

export function ResultsScenarios({
  test,
}: {
  test: AssessmentTestSessionDetailResponse | null | undefined;
}) {
  const otherSteps =
    test?.steps.filter((step) => step.label !== 'Self Assessment') ?? [];

  const [expandedTestIds, setExpandedTestIds] = React.useState<Array<string>>(
    otherSteps.map((step) => step.id)
  );

  const scenarios =
    otherSteps.map((step) => ({
      step,
      sessionId: test?.assessmentTestSessionId.toString() ?? '',
      didStartTest: test?.startedAt !== null,
      answer: test?.answers.find((a) => a.id === step.id),
      stepReview:
        test?.results?.scenarioEvaluations.find(
          (evaluation) => evaluation.scenarioId === step.id
        ) ?? null,
      selfAssessmentCalibration:
        test?.results?.selfAssessmentCalibration ?? null,
      testDimensions: test?.dimensions ?? [],
    })) ?? [];

  const isStarted = test?.startedAt !== null;

  return (
    <SectionWrapper>
      <SectionHeader>
        <Title>Scenarios Evaluation</Title>
        <Caption>Performance analysis across technical scenarios</Caption>
      </SectionHeader>
      {scenarios.map((scenario) => (
        <CollapsibleSection
          key={scenario.step.id}
          headerLeft={
            <CollapsibleSectionHeader>
              {scenario.step.title}
            </CollapsibleSectionHeader>
          }
          headerRight={
            <CollapsibleSectionLabel>
              {scenario.step.label}
            </CollapsibleSectionLabel>
          }
          expanded={expandedTestIds.includes(scenario.step.id)}
          onToggle={() => {
            setExpandedTestIds((prev) => {
              if (prev.includes(scenario.step.id)) {
                return prev.filter((id) => id !== scenario.step.id);
              }
              return [...prev, scenario.step.id];
            });
          }}
        >
          <StepWrapper>
            <ScenarioSummary {...scenario} isStarted={isStarted} />
            <StepQuestionsResultsTable {...scenario} />
          </StepWrapper>
        </CollapsibleSection>
      ))}
    </SectionWrapper>
  );
}

const StepWrapper = styled.div`
  width: 100%;
  height: 100%;
`;

function ScenarioSummary({
  answer,
  step,
  isStarted,
}: {
  step: AssessmentTestStep;
  stepReview: AssessmentTestResult['scenarioEvaluations'][number] | null;
  answer: AssessmentTestStepAnswer | undefined;
      testDimensions: AssessmentTestSessionDetailResponse['dimensions'];
  isStarted: boolean;
}) {
  const secondsSpent =
    computeTimeFromIntervals(answer?.introduction?.timeIntervals ?? []) +
    (answer?.sections ?? []).reduce((acc, section) => {
      return acc + computeTimeFromIntervals(section?.timeIntervals ?? []);
    }, 0);
  const numAnsweredQuestions =
    step?.sections.filter(
      (section) => answer?.sections.find((a) => a.id === section.id)?.isAnswered
    ).length ?? 0;

  return (
    <SummaryWrapper>
      <Row style={{ gap: 64, alignItems: 'flex-start' }}>
        <MetricSection data-tooltip-id="step-results-summary">
          <MetricLabel>Time Spent</MetricLabel>
          <MetricValue>
            {isStarted ? formatDuration(secondsSpent) : '-'}
          </MetricValue>
        </MetricSection>
        <MetricSection>
          <MetricLabel>Progress</MetricLabel>
          <ProgressTrack>
            <ProgressFill
              width={
                ((numAnsweredQuestions ?? 0) / (step?.sections.length ?? 0)) *
                100
              }
            />
          </ProgressTrack>
          <Caption>
            {`${numAnsweredQuestions}/${step?.sections.length} questions answered`}
          </Caption>
        </MetricSection>
      </Row>
    </SummaryWrapper>
  );
}

const SummaryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.bgActive};
  background-color: ${({ theme }) => theme.colors.bgDefaultSecondary};
  padding: 24px;
`;
